import { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import DateField from '../../../molecules/DateField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import {
    BusinessReference,
    type CreateClaimItemPayload,
    type ObligationType,
    type SelectOption,
} from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';

interface LegalCaseClaimItemFormProps {
    businessReferenceOptions: SelectOption[];
    obligationTypeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: CreateClaimItemPayload) => Promise<boolean>;
    onCancel: () => void;
}

// Campos que têm binding de erro inline (errorMessage=) no JSX. O bloco residual mostra só os
// erros SEM campo próprio (ex.: "items" da regra anti-duplicação INSTALLMENT+GUARANTEE), para
// não duplicar (inline + residual). Precisa espelhar exatamente os campos com errorMessage abaixo.
const inlineErrorFields = new Set([
    'titleNumber', 'businessReference', 'amountAtFiling', 'paidAmount',
    'dueDate', 'obligationType', 'externalReference', 'description',
]);
const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !inlineErrorFields.has(e.fieldName));

// Formulário de adição de um título ao claim. Só JSX + estado local simples (o form é curto e
// linear). A regra do GUARANTEE vive no backend; o form só oferece o select e mostra o 422.
const LegalCaseClaimItemForm = ({
    businessReferenceOptions,
    obligationTypeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseClaimItemFormProps) => {
    const [titleNumber, setTitleNumber] = useState('');
    const [businessReference, setBusinessReference] = useState<BusinessReference>(BusinessReference.INSTALLMENT);
    const [amountAtFiling, setAmountAtFiling] = useState(0);
    const [dueDate, setDueDate] = useState('');
    const [paidAmount, setPaidAmount] = useState(0);
    const [obligationType, setObligationType] = useState<ObligationType | ''>('');
    const [externalReference, setExternalReference] = useState('');
    const [description, setDescription] = useState('');

    const canSubmit = titleNumber.trim().length > 0 && amountAtFiling > 0;

    const handleSubmit = async () => {
        const ok = await onSubmit({
            titleNumber: titleNumber.trim(),
            businessReference,
            amountAtFiling,
            dueDate: dueDate || undefined,
            paidAmount: paidAmount > 0 ? paidAmount : undefined,
            obligationType: obligationType || undefined,   // '' → undefined (tipo estreitado no select)
            externalReference: externalReference.trim() || undefined,
            description: description.trim() || undefined,
        });
        if (ok) onCancel();
    };

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextEdit
                    label="Número do título"
                    value={titleNumber}
                    onChange={(e: any) => setTitleNumber(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'titleNumber')}
                />
                <SelectTextField
                    label="Referência de negócio"
                    items={businessReferenceOptions}
                    value={businessReference}
                    onChange={(e) => setBusinessReference(e.target.value as BusinessReference)}
                    errorMessage={getErrorMessage(errorFields, 'businessReference')}
                />
            </div>

            {businessReference === BusinessReference.GUARANTEE && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                    Garantia entra à parte, fora do total. Não pode conviver com uma parcela da
                    mesma operação.
                </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CurrencyInput
                    label="Valor no ajuizamento"
                    prefix="R$"
                    fractionDigits={2}
                    value={amountAtFiling}
                    onChange={setAmountAtFiling}
                    errorMessage={getErrorMessage(errorFields, 'amountAtFiling')}
                />
                <CurrencyInput
                    label="Valor pago (opcional)"
                    prefix="R$"
                    fractionDigits={2}
                    value={paidAmount}
                    onChange={setPaidAmount}
                    errorMessage={getErrorMessage(errorFields, 'paidAmount')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DateField
                    label="Vencimento (opcional)"
                    value={dueDate}
                    onChange={setDueDate}
                    errorMessage={getErrorMessage(errorFields, 'dueDate')}
                />
                <SelectTextField
                    label="Tipo da obrigação (opcional)"
                    items={[{ value: '', label: '—' }, ...obligationTypeOptions]}
                    value={obligationType}
                    // O select só oferece '' (—) ou os valores do enum, então o cast é seguro.
                    onChange={(e) => setObligationType(e.target.value as ObligationType | '')}
                    errorMessage={getErrorMessage(errorFields, 'obligationType')}
                />
            </div>

            <TextEdit
                label="Referência externa (CPR-F, CD, PJ, operação)"
                value={externalReference}
                onChange={(e: any) => setExternalReference(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'externalReference')}
            />

            <TextEdit
                label="Observação"
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isSaving || !canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Adicionar título'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseClaimItemForm;
